/**
 * 時間前市場 板発注タブの売買列表示順の 2 択トグル (AC-SPS-032 / AC-SPS-033 / AC-BIN-026)。
 * 板の見出しの右端に置く。 共通部品 `IntradayBoardInterconPanel` が `columnOrder` を渡された時だけ描く。
 */
import { useTranslation } from 'react-i18next'

import {
  type SegmentedControlOption,
  Text,
  VisuallyHidden,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'

/**
 * 板の売買列表示順 (AC-SPS-032 / AC-SPS-033)。 `sellLeft` = 売り左・買い右 (既定)。
 * 個人設定 (proto / backend / DB) の `OrderBookColumnOrder` (SELL_LEFT / BUY_LEFT) を、 板の描画用に
 * 写したもの。 変換は `useBoardColumnOrder` の 1 か所だけで行う。
 */
export type BoardColumnOrder = 'sellLeft' | 'buyLeft'

/** `IntradayBoardInterconPanel` の `columnOrder` に渡す値 (`useBoardColumnOrder` が組み立てる)。 */
export type BoardColumnOrderControl = {
  value: BoardColumnOrder
  onChange: (next: BoardColumnOrder) => void
  /** あればトグルは押せず、 その文言を tooltip に出す (個人設定の取得中・取得失敗。 D6)。 */
  disabledReason?: string
  /**
   * 個人設定の取得が決着するまで true。 板のテーブルだけを読み込み表示にする (D8)。
   * 渡し忘れると初回描画の保護が黙って外れるので必須にしている。
   */
  isSettling: boolean
}

type BoardSide = 'sell' | 'buy'

/** 見出し右端の 2 択トグル (板発注タブ専用)。 見た目は「売|買」「買|売」、 名前は「売り左・買い右」等。 */
export const BoardColumnOrderToggle = ({
  value,
  onChange,
  disabledReason,
  isDisabled = false,
}: {
  value: BoardColumnOrder
  onChange: (next: BoardColumnOrder) => void
  disabledReason: string | undefined
  /**
   * 板ごと操作不可の間 (市場未開場) true。 板の減光は pointer-events だけで止めており、
   * radio の focus と矢印キーは止まらないので、 トグル自体も disabled にする (AC-BIN-055)。
   * 理由は板の減光で伝わるので tooltip は付けない。
   */
  isDisabled?: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  const disabled = isDisabled || disabledReason !== undefined
  const sellText = t('page:biddingIntraday.market.board.sell')
  const buyText = t('page:biddingIntraday.market.board.buy')
  // 見た目は「売|買」「買|売」(選択中のセグメントだけ売り・買いを板の価格セルと同じ色にする)。
  // accessible name は全文を visually-hidden で持つ (可視ラベルに Tooltip / title は重ねない)。
  const optionLabel = (
    full: string,
    first: BoardSide,
    selected: boolean,
  ): React.ReactNode => {
    const second: BoardSide = first === 'sell' ? 'buy' : 'sell'
    const text = (side: BoardSide): string =>
      side === 'sell' ? sellText : buyText
    // 選択中のセグメントだけ、 売り・買いを板の価格セルと同じ色にする。
    const colorOf = (side: BoardSide): string | undefined =>
      selected ? (side === 'sell' ? 'Text.offer' : 'Text.bid') : undefined
    return (
      <span>
        <span aria-hidden="true">
          <Text as="span" color={colorOf(first)}>
            {text(first)}
          </Text>
          <Text as="span" color="Text.lowEmphasis" fontWeight="normal">
            |
          </Text>
          <Text as="span" color={colorOf(second)}>
            {text(second)}
          </Text>
        </span>
        <VisuallyHidden>{full}</VisuallyHidden>
      </span>
    )
  }
  const options: Array<SegmentedControlOption<BoardColumnOrder>> = [
    {
      value: 'sellLeft',
      disabled,
      label: optionLabel(
        t('page:biddingIntraday.market.board.columnOrder.sellLeft'),
        'sell',
        value === 'sellLeft',
      ),
    },
    {
      value: 'buyLeft',
      disabled,
      label: optionLabel(
        t('page:biddingIntraday.market.board.columnOrder.buyLeft'),
        'buy',
        value === 'buyLeft',
      ),
    },
  ]
  return (
    <ButtonTooltip content={disabledReason}>
      <AccessibleSegmentedControl
        aria-label={t(
          'page:biddingIntraday.market.board.columnOrder.groupLabel',
        )}
        name="intraday-board-column-order"
        onSelect={onChange}
        options={options}
        selected={value}
      />
    </ButtonTooltip>
  )
}
